/**
 * Resolve the SKU id column display name from product schema (for Approvals table).
 */
import { useMemo } from "react";
import type { TFunction } from "i18next";
import type { PIMProduct } from "../../../../../types/types.PIM";

const SKU_ID_NAME_VARIANTS = ["sku_id", "sku id", "sku"];

export function useSkuIdColumnHeader({
  productSchema,
  t,
  fallback = "ionicPIM SKU ID",
}: {
  productSchema?: PIMProduct["product_schema"];
  t: TFunction;
  fallback?: string;
}): string {
  return useMemo(() => {
    const columns = productSchema?.collections?.find(
      (col) => col.object_type === "skus"
    )?.columns;
    if (!columns) {
      return fallback;
    }

    const byType = columns.find((col) => col.object_type === "sku_id");
    if (byType) {
      return byType.display_name
        ? t([byType.display_name])
        : byType.name ?? fallback;
    }

    for (const nameVariant of SKU_ID_NAME_VARIANTS) {
      const col = columns.find(
        (c) =>
          c.name?.toLowerCase() === nameVariant ||
          c.display_name?.toLowerCase() === nameVariant
      );
      if (col) {
        return col.display_name ? t([col.display_name]) : col.name ?? fallback;
      }
    }
    return fallback;
  }, [fallback, productSchema, t]);
}
